import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';

// react-helmet-async internally may use react-is. 
// With React 19, there can be version mismatches.
// This component provides standard SEO meta tags.

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogUrl?: string;
  canonical?: string;
  jsonLd?: object;
}

import { t } from '../../i18n';

const SEO: React.FC<SEOProps> = ({
  title,
  description,
  keywords,
  ogUrl,
  canonical,
  jsonLd,
}) => {
  const { lang } = useLanguage();
  
  const siteTitle = 'XLEVEN';
  const defaultDescription = t(lang, 'seoDefaultDescription');
  const defaultKeywords = t(lang, 'seoDefaultKeywords');
  const siteUrl = 'https://www.xleven.co';

  const fullTitle = title ? `${title} | ${siteTitle}` : `XLEVEN | ${t(lang, 'seoHomeTitle')}`;

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      <meta name="description" content={(description || defaultDescription).substring(0, 160)} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content="XLEVEN" />
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      
      {/* Canonical */}
      <link rel="canonical" href={canonical || `${siteUrl}${window.location.pathname}`} />

      {/* Open Graph */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content="XLEVEN" />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={(description || defaultDescription).substring(0, 160)} />
      <meta property="og:url" content={ogUrl || `${siteUrl}${window.location.pathname}`} />
      <meta property="og:image" content="https://dvcrglecwqmqcrioedln.supabase.co/storage/v1/object/public/xleven/thum.png" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={(description || defaultDescription).substring(0, 160)} />

      {/* Structured Data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(jsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
